import { useState, type AnchorHTMLAttributes, type MouseEvent } from 'react';
import { useTranslations } from 'use-intl';
import { Button, Dialog, DialogFooter } from '@leanin/ui';

// The one interstitial mechanism for user- and member-entered links (design
// 2026-08-06 Project B): render the anchor with the same attributes the raw <a>
// carried, intercept click, and gate cross-origin navigation behind a confirm
// dialog. Same-origin URLs (and relative hrefs) navigate exactly as before, and
// only http(s) web navigations are gated — mailto:/tel: and other native-handler
// schemes are not "a site outside Lean In Connected" and pass straight through.
// The origin check runs at click time so rendering stays render-to-string safe.
// Auxclick (middle-click) is not interceptable here and falls back to the
// anchor's own noopener/noreferrer attributes.
type ExternalLinkProps = Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> & {
  readonly href: string;
};

export function ExternalLink({ href, children, onClick, ...rest }: ExternalLinkProps) {
  const [pendingUrl, setPendingUrl] = useState<URL | null>(null);

  const handleClick = (event: MouseEvent<HTMLAnchorElement>): void => {
    onClick?.(event);
    if (event.defaultPrevented) return;
    let url: URL;
    try {
      url = new URL(href, window.location.href);
    } catch {
      return;
    }
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return;
    if (url.origin === window.location.origin) return;
    event.preventDefault();
    setPendingUrl(url);
  };

  return (
    <>
      <a href={href} target="_blank" rel="noopener noreferrer" {...rest} onClick={handleClick}>
        {children}
      </a>
      {pendingUrl !== null ? (
        <ExternalLinkDialog url={pendingUrl} onClose={() => setPendingUrl(null)} />
      ) : null}
    </>
  );
}

// Mounted only after a gated click, so the translations hook never runs during
// plain body rendering (keeps render-to-string paths free of IntlProvider).
function ExternalLinkDialog({
  url,
  onClose,
}: {
  readonly url: URL;
  readonly onClose: () => void;
}) {
  const t = useTranslations('Common.externalLink');
  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title={t('title')}
      description={t('description', { host: url.host })}
      size="sm"
    >
      <DialogFooter>
        <Button variant="ghost" onClick={onClose}>
          {t('cancel')}
        </Button>
        <Button
          variant="primary"
          onClick={() => {
            window.open(url.href, '_blank', 'noopener,noreferrer');
            onClose();
          }}
        >
          {t('continue')}
        </Button>
      </DialogFooter>
    </Dialog>
  );
}
